import React, { useState } from "react";
import {
  Card,
  CardBody,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Badge,
  Button,
  Table,
  Spinner,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Alert,
} from "reactstrap";
import { Link } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";

import {
  useGetPurchaseOrderByRequestIdQuery,
  useCreatePOFromUndeliverableMutation,
  useGetSuppliersQuery,
  useUpdatePurchaseOrderByIdMutation,
} from "slices/InventoryManagement/inventoryManagementSlice";
import PriceViewComponent from "../CommonComponents/PriceViewComponent";
import ProductThumbnail from "../CommonComponents/ProductThumbnail";
import { IsValidPermissions } from "Routes/AuthProtected";

interface PurchaseOrder {
  id: number;
  purchase_request: number;
  purchase_request_number: string;
  supplier: number;
  supplier_name: string;
  po_status: string;
  inventory_purchase_order_number: string;
  delivery_status: string;
  delivery_notes: string;
   approval_status_name?: string;
   notes?: any;
  items_count: number;
  items: any[];
  created_at: string;
  modified_at: string;
}

interface PurchaseOrderListProps {
  purchaseRequestId: string;
}

const PurchaseOrderList: React.FC<PurchaseOrderListProps> = ({ purchaseRequestId }) => {
  const [openAccordion, setOpenAccordion] = useState<string[]>([]);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  // PO cut-off: Create new PO from undeliverable
  const [createPOModal, setCreatePOModal] = useState<{
    open: boolean;
    po: PurchaseOrder | null;
    selectedSupplierId: number | null;
    selectedItemIds: number[];
    itemUnitPrices: Record<number, string>;
    notes: string;
  }>({
    open: false,
    po: null,
    selectedSupplierId: null,
    selectedItemIds: [],
    itemUnitPrices: {},
    notes: "",
  });

  const [createPOFromUndeliverable, { isLoading: isCreatingPO }] =
    useCreatePOFromUndeliverableMutation();
  const { data: suppliersData } = useGetSuppliersQuery({ page: 1, pageSize: 1000 });
  const suppliers = suppliersData?.results || [];

  const [updatePurchaseOrderById, { isLoading: isUpdatingNotes }] =
    useUpdatePurchaseOrderByIdMutation();

  const [editNotesModal, setEditNotesModal] = useState<{
    open: boolean;
    po: PurchaseOrder | null;
    notesText: string;
  }>({
    open: false,
    po: null,
    notesText: "",
  });

  // Use the hook to fetch purchase orders
  const {
    data: purchaseOrdersData,
    isLoading,
    error,
    refetch: refetchPurchaseOrders,
  } = useGetPurchaseOrderByRequestIdQuery(purchaseRequestId);

  const toggleAccordion = (poNumber: string) => {
    setOpenAccordion(prev => 
      prev.includes(poNumber) 
        ? prev.filter(item => item !== poNumber)
        : [...prev, poNumber]
    );
  };

  const toggleItemExpansion = (poId: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(poId)) {
      newExpanded.delete(poId);
    } else {
      newExpanded.add(poId);
    }
    setExpandedItems(newExpanded);
  };

  const getSupplierQuantityForItem = (item: any) => {
    const mode = String(item?.source_mode || "").toLowerCase();
    if (mode === "split") {
      return parseFloat(item?.supplier_allocation_quantity || "0") || 0;
    }
    if (mode === "stock") {
      return 0;
    }
    const supplierQty = parseFloat(item?.supplier_allocation_quantity || "0") || 0;
    if (supplierQty > 0) return supplierQty;
    return parseFloat(item?.quantity || "0") || 0;
  };

  const canEditNotes = (po: PurchaseOrder) => {
    const approval = (po.approval_status_name || "").toString().toLowerCase();
    return po.po_status === "submitted" && approval.startsWith("pending");
  };

  const handleOpenEditNotes = (po: PurchaseOrder) => {
    const raw = po.notes;
    let initial = "";

    if (Array.isArray(raw)) {
      initial = raw.join("\n");
    } else if (typeof raw === "string") {
      initial = raw;
    } else if (raw && typeof raw === "object") {
      initial = Object.values(raw)
        .map((v) => String(v))
        .join("\n");
    }

    setEditNotesModal({
      open: true,
      po,
      notesText: initial,
    });
  };

  const handleCloseEditNotes = () => {
    setEditNotesModal({
      open: false,
      po: null,
      notesText: "",
    });
  };

  const handleSaveNotes = async () => {
    if (!editNotesModal.po) return;

    const trimmed = editNotesModal.notesText.trim();
    if (!trimmed) {
      toast.error("Notes cannot be empty.");
      return;
    }

    const lines = trimmed
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    try {
      await updatePurchaseOrderById({
        id: editNotesModal.po.id,
        values: { notes: lines },
      }).unwrap();
      toast.success("PO notes updated successfully.");
      handleCloseEditNotes();
      refetchPurchaseOrders();
    } catch (error: any) {
      // Backend will enforce that only notes are editable when submitted/pending
      const msg =
        error?.data?.error ||
        error?.data?.details ||
        "Failed to update notes. Please try again.";
      toast.error(msg);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { color: string; label: string } } = {
      draft: { color: "secondary", label: "DRAFT" },
      submitted: { color: "warning", label: "SUBMITTED" },
      approved: { color: "success", label: "APPROVED" },
      rejected: { color: "danger", label: "REJECTED" },
      resubmit: { color: "info", label: "RESUBMIT" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      label: status,
    };
    return <Badge color={config.color}>{config.label}</Badge>;
  };

  const getUndeliverableItems = (po: PurchaseOrder) =>
    (po.items || []).filter((item: any) => item.item_undeliverable);

  const handleOpenCreatePOFromUndeliverable = (po: PurchaseOrder) => {
    const undeliverable = getUndeliverableItems(po);
    const prices: Record<number, string> = {};
    undeliverable.forEach((item: any) => {
      const balQty = parseFloat(item.balance_quantity) || 0;
      const balCost = parseFloat(item.balance_cost) || 0;
      prices[item.id] = balQty > 0 ? (balCost / balQty).toFixed(2) : "0";
    });
    setCreatePOModal({
      open: true,
      po,
      selectedSupplierId: null,
      selectedItemIds: undeliverable.map((i: any) => i.id),
      itemUnitPrices: prices,
      notes: "",
    });
  };

  const handleCreatePOFromUndeliverable = async () => {
    const { po, selectedSupplierId, selectedItemIds, itemUnitPrices, notes } = createPOModal;
    if (!po || !selectedSupplierId || selectedItemIds.length === 0) {
      toast.error("Please select a supplier and at least one item");
      return;
    }
    try {
      const item_costs = selectedItemIds.map((id) => {
        const item = getUndeliverableItems(po).find((i: any) => i.id === id);
        const balQty = item ? parseFloat(item.balance_quantity) || 0 : 0;
        const balCost = item ? parseFloat(item.balance_cost) || 0 : 0;
        const defaultPrice = balQty > 0 ? balCost / balQty : 0;
        const unitPrice = itemUnitPrices[id] != null ? parseFloat(String(itemUnitPrices[id])) : defaultPrice;
        return { item_id: id, unit_price: unitPrice >= 0 ? unitPrice : defaultPrice };
      });
      await createPOFromUndeliverable({
        poId: po.id,
        body: {
          new_supplier_id: selectedSupplierId,
          item_ids: selectedItemIds,
          item_costs: item_costs.length > 0 ? item_costs : undefined,
          notes: notes || undefined,
        },
      }).unwrap();
      toast.success("New PO created from undeliverable items");
      setCreatePOModal({ open: false, po: null, selectedSupplierId: null, selectedItemIds: [], itemUnitPrices: {}, notes: "" });
      refetchPurchaseOrders();
    } catch (err: any) {
      toast.error(err?.data?.error || "Failed to create new PO");
    }
  };

  const getDeliveryStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { color: string; label: string } } = {
      pending: { color: "warning", label: "PENDING" },
      in_progress: { color: "info", label: "IN PROGRESS" },
      delivered: { color: "success", label: "DELIVERED" },
    };

    const config = statusConfig[status] || {
      color: "secondary",
      label: status,
    };
    return <Badge color={config.color}>{config.label}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <Spinner color="primary" />
        <div className="mt-2 text-muted">Loading purchase orders...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <i className="ri-error-warning-line align-middle me-2"></i>
        Failed to load purchase orders.
      </div>
    );
  }

  if (!purchaseOrdersData?.purchase_orders || purchaseOrdersData.purchase_orders.length === 0) {
    return (
      <div className="text-center py-5">
        <i className="ri-inbox-line display-4 text-muted opacity-50"></i>
        <h5 className="mt-3 text-muted">No Purchase Orders Created Yet</h5>
        <p className="text-muted mb-4">
          There are no purchase orders created for this purchase request yet.
        </p>
      </div>
    );
  }

  return (
    <div className="purchase-order-list">
      <div className="mb-3">
        <h6 className="text-muted">
          Total Purchase Orders: <Badge color="primary">{purchaseOrdersData.total_purchase_orders}</Badge>
        </h6>
      </div>

      <Accordion open={openAccordion} toggle={toggleAccordion}>
        {purchaseOrdersData.purchase_orders.map((po: PurchaseOrder, index: number) => (
          <AccordionItem key={po.id}>
            <AccordionHeader targetId={po.inventory_purchase_order_number}>
              <div className="d-flex justify-content-between align-items-center w-100 me-3">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <i className="ri-file-list-line text-primary fs-18 me-3"></i>
                  </div>
                  <div className="flex-grow-1 text-start">
                    {/* PO Number as Link */}
                    <h6 className="mb-1">
                      <Link 
                        to={`/purchase-order-detail/${po.inventory_purchase_order_number}`}
                        className="text-decoration-none text-dark"
                        onClick={(e) => e.stopPropagation()} // Prevent accordion toggle when clicking link
                      >
                        {po.inventory_purchase_order_number}
                        <i className="ri-external-link-line ms-1 fs-12 text-primary"></i>
                      </Link>
                    </h6>
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      <small className="text-muted">
                        Supplier: <strong>{po.supplier_name}</strong>
                      </small>
                      <small className="text-muted">•</small>
                      <small className="text-muted">
                        Items: <strong>{po.items_count}</strong>
                      </small>
                      <small className="text-muted">•</small>
                      <small className="text-muted">
                        Created: {formatDate(po.created_at)}
                      </small>
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  {getStatusBadge(po.po_status)}
                  {getDeliveryStatusBadge(po.delivery_status)}
                </div>
              </div>
            </AccordionHeader>
            <AccordionBody accordionId={po.inventory_purchase_order_number}>
              {/* PO Details */}
              <div className="mb-4 p-3 bg-light rounded">
                <Row className="g-3">
                  <Col md={6}>
                    <div>
                      <strong>PO Number:</strong>{" "}
                      <Link 
                        to={`/purchase-order-detail/${po.inventory_purchase_order_number}`}
                        className="text-primary text-decoration-none fw-bold"
                      >
                        {po.inventory_purchase_order_number}
                        <i className="ri-external-link-line ms-1 fs-12"></i>
                      </Link>
                    </div>
                    <div>
                      <strong>Supplier:</strong> {po.supplier_name}
                    </div>
                    <div>
                      <strong>Status:</strong> {getStatusBadge(po.po_status)}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div>
                      <strong>Delivery Status:</strong> {getDeliveryStatusBadge(po.delivery_status)}
                    </div>
                    <div>
                      <strong>Items Count:</strong> {po.items_count}
                    </div>
                    <div>
                      <strong>Created:</strong> {formatDate(po.created_at)}
                    </div>
                  </Col>
                </Row>
                {po.delivery_notes && (
                  <div className="mt-2">
                    <strong>Delivery Notes:</strong> {po.delivery_notes}
                  </div>
                )}
              </div>

              {/* Items Section - Always Show Table View */}
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Order Items ({po.items_count})</h6>
                  <div className="d-flex gap-2">
                    <Button
                      color="link"
                      size="sm"
                      onClick={() => toggleItemExpansion(po.id)}
                      className="text-decoration-none"
                    >
                      {expandedItems.has(po.id) ? "Show Less" : "Show More Details"}
                    </Button>
                    <Link 
                      to={`/purchase-order-detail/${po.inventory_purchase_order_number}`}
                      className="btn btn-primary btn-sm"
                    >
                      <i className="ri-eye-line me-1"></i> View Full Details
                    </Link>
                  </div>
                </div>

                {/* Always show table view */}
                <div className="table-responsive">
                  <Table bordered className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Product</th>
                        <th className="text-center">Quantity</th>
                        <th className="text-center">Unit Cost</th>
                        <th className="text-center">Total Cost</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Show all items if expanded, otherwise show limited items */}
                      {(expandedItems.has(po.id) ? po.items : po.items.slice(0, 5)).map((item: any) => (
                        <tr key={item.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              {item.thumbnail_url && (
                                <img
                                  src={item.thumbnail_url}
                                  alt={item.product_name}
                                  className="rounded me-3"
                                  style={{ width: "40px", height: "40px", objectFit: "cover" }}
                                />
                              )}
                              <div>
                                <div className="fw-medium">{item.product_name}</div>
                                <small className="text-muted">{item.category_name}</small>
                                {!!item?.stock_allocations?.length && (
                                  <div className="mt-1">
                                    {item.stock_allocations.map((alloc: any) => (
                                      <div key={alloc.id} className="text-muted fs-11">
                                        Stock Ref: {alloc.source_stock_reference || alloc.allocation_no || `Alloc ${alloc.id}`} | Qty {alloc.allocated_from_stock_qty}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="text-center">
                            {["split", "stock"].includes(String(item?.source_mode || "").toLowerCase())
                              ? getSupplierQuantityForItem(item)
                              : item.quantity}{" "}
                            {item.measurement_name}
                            {["split", "stock"].includes(String(item?.source_mode || "").toLowerCase()) && (
                              <div className="text-muted fs-11 mt-1">
                                Supplier: {getSupplierQuantityForItem(item)} | Stock: {item.stock_allocation_quantity || 0}
                              </div>
                            )}
                             {!!item?.stock_allocations?.length && (
                                  <div className="mt-1">
                                    {item.stock_allocations.map((alloc: any) => (
                                      <div key={alloc?.id} className="text-black fs-11">
                                        <b>
                                        Stock Ref: {alloc.source_stock_reference || alloc.allocation_no || `Alloc ${alloc.id}`} | Qty {alloc.allocated_from_stock_qty}
                                        </b>
                                      </div>
                                    ))}
                                  </div>
                                )}
                          </td>
                          <td className="text-center">
                            <PriceViewComponent
                              price={(
                                parseFloat(item.effective_unit_price || "0") 
                              ).toFixed(2)}
                            />
                          </td>
                          <td className="text-center fw-bold text-success">
                          <PriceViewComponent price={parseFloat(item.total_item_cost).toFixed(2)} />
                          </td>
                          <td className="text-center">
                            <div className="d-flex flex-column align-items-center gap-1">
                              <Badge
                                color={
                                  item.status === "delivered"
                                    ? "success"
                                    : item.status === "approved"
                                    ? "info"
                                    : "warning"
                                }
                              >
                                {item.status}
                              </Badge>
                              {item.item_undeliverable && (
                                <Badge color="danger" className="fs-11">Undeliverable</Badge>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Show "Show More" message if there are more items */}
                {!expandedItems.has(po.id) && po.items.length > 5 && (
                  <div className="text-center mt-3 p-3 border-top">
                    <Button
                      color="link"
                      onClick={() => toggleItemExpansion(po.id)}
                      className="text-primary text-decoration-none"
                    >
                      <i className="ri-add-line me-1"></i>
                      Show {po.items.length - 5} more items
                    </Button>
                  </div>
                )}
              </div>

              {/* TEMPORARILY COMMENTED - Undeliverable items section - can use later
              {getUndeliverableItems(po).length > 0 && IsValidPermissions(["ADD_PURCHASE_ORDER"]) && (
                <div className="mb-3 p-3 bg-danger bg-opacity-10 border border-danger rounded">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <Badge color="danger" className="me-2">
                        {getUndeliverableItems(po).length} Undeliverable
                      </Badge>
                      <span className="text-muted small">Select new supplier to create replacement PO</span>
                    </div>
                    <Button
                      color="danger"
                      size="sm"
                      onClick={() => handleOpenCreatePOFromUndeliverable(po)}
                    >
                      <i className="ri-add-circle-line me-1"></i> Create New PO from Undeliverable
                    </Button>
                  </div>
                </div>
              )}
              */}

              {/* Action Buttons */}
              <div className="d-flex gap-2 justify-content-between border-top pt-3">
                <div className="d-flex gap-2">
                  <Link 
                    to={`/purchase-order-detail/${po.inventory_purchase_order_number}`}
                    className="btn btn-primary btn-sm"
                  >
                    <i className="ri-eye-line me-1"></i> View Full Details
                  </Link>
                  {/* <Button color="outline-secondary" size="sm">
                    <i className="ri-download-line me-1"></i> Export
                  </Button> */}
                </div>
                <div className="d-flex gap-2">
                  {canEditNotes(po) && (
                    <Button
                      color="warning"
                      size="sm"
                      onClick={() => handleOpenEditNotes(po)}
                    >
                      <i className="ri-edit-line me-1"></i>
                      Edit Notes
                    </Button>
                  )}
                </div>
              </div>
            </AccordionBody>
          </AccordionItem>
        ))}
      </Accordion>

      {/* TEMPORARILY COMMENTED - Create PO from Undeliverable Modal - can use later
      <Modal
        isOpen={createPOModal.open}
        toggle={() => !isCreatingPO && setCreatePOModal((p) => ({ ...p, open: false }))}
        size="lg"
      >
        <ModalHeader toggle={() => !isCreatingPO && setCreatePOModal((p) => ({ ...p, open: false }))}>
          <i className="ri-add-circle-line me-2"></i>
          Create New PO from Undeliverable Items
          {createPOModal.po && (
            <Badge color="secondary" className="ms-2">
              {createPOModal.po.inventory_purchase_order_number}
            </Badge>
          )}
        </ModalHeader>
        <ModalBody>
          {createPOModal.po && (
            <>
              <Alert color="info" className="mb-3">
                Select a new supplier for the undeliverable items. Balance quantities and costs will be used.
              </Alert>
              <FormGroup>
                <Label>New Supplier <span className="text-danger">*</span></Label>
                <Select
                  options={suppliers.map((s: any) => ({
                    value: s.id,
                    label: `${s.name}${s.email ? ` (${s.email})` : ""}`,
                  }))}
                  value={
                    createPOModal.selectedSupplierId
                      ? suppliers
                          .filter((s: any) => s.id === createPOModal.selectedSupplierId)
                          .map((s: any) => ({
                            value: s.id,
                            label: `${s.name}${s.email ? ` (${s.email})` : ""}`,
                          }))[0]
                      : null
                  }
                  onChange={(opt: any) =>
                    setCreatePOModal((p) => ({ ...p, selectedSupplierId: opt?.value || null }))
                  }
                  placeholder="Select supplier..."
                  isDisabled={isCreatingPO}
                />
              </FormGroup>
              <FormGroup>
                <Label>Items ({createPOModal.selectedItemIds.length})</Label>
                <div className="border rounded p-3" style={{ maxHeight: "200px", overflowY: "auto" }}>
                  {getUndeliverableItems(createPOModal.po).map((item: any) => {
                    const balQty = parseFloat(item.balance_quantity) || 0;
                    const balCost = parseFloat(item.balance_cost) || 0;
                    const defaultUnit = balQty > 0 ? (balCost / balQty).toFixed(2) : "0";
                    const unitPrice = createPOModal.itemUnitPrices[item.id] ?? defaultUnit;
                    const lineTotal = (parseFloat(unitPrice) * balQty).toFixed(2);
                    return (
                      <div key={item.id} className="d-flex align-items-center justify-content-between p-2 border rounded mb-2">
                        <div className="d-flex align-items-center flex-grow-1">
                          <Input
                            type="checkbox"
                            checked={createPOModal.selectedItemIds.includes(item.id)}
                            onChange={(e) =>
                              setCreatePOModal((p) => ({
                                ...p,
                                selectedItemIds: e.target.checked
                                  ? [...p.selectedItemIds, item.id]
                                  : p.selectedItemIds.filter((id) => id !== item.id),
                              }))
                            }
                            disabled={isCreatingPO}
                            className="me-2"
                          />
                          <ProductThumbnail
                            attachment_urls={item?.attachment_urls}
                            alt={item?.product_name || ""}
                            size="sm"
                          />
                          <div className="ms-2">
                            <strong>{item.product_name}</strong>
                            <div className="text-muted fs-12">
                              Balance: {item.balance_quantity || 0} | Unit:{" "}
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={unitPrice}
                                onChange={(e) =>
                                  setCreatePOModal((p) => ({
                                    ...p,
                                    itemUnitPrices: { ...p.itemUnitPrices, [item.id]: e.target.value },
                                  }))
                                }
                                disabled={isCreatingPO}
                                className="d-inline-block"
                                style={{ width: "80px" }}
                              />{" "}
                              → Total: {lineTotal}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </FormGroup>
              <FormGroup>
                <Label>Notes (Optional)</Label>
                <Input
                  type="textarea"
                  rows={2}
                  value={createPOModal.notes}
                  onChange={(e) => setCreatePOModal((p) => ({ ...p, notes: e.target.value }))}
                  placeholder="Notes for the new PO..."
                  disabled={isCreatingPO}
                />
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => !isCreatingPO && setCreatePOModal((p) => ({ ...p, open: false }))} disabled={isCreatingPO}>
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={handleCreatePOFromUndeliverable}
            disabled={isCreatingPO || !createPOModal.selectedSupplierId || createPOModal.selectedItemIds.length === 0}
          >
            {isCreatingPO ? "Creating..." : "Create New PO"}
          </Button>
        </ModalFooter>
      </Modal>
      */}

      {/* Edit Notes Modal */}
      <Modal isOpen={editNotesModal.open} toggle={handleCloseEditNotes}>
        <ModalHeader toggle={handleCloseEditNotes}>
          Edit PO Notes
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label className="fw-semibold">
              Notes / Instructions <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              rows={5}
              value={editNotesModal.notesText}
              onChange={(e) =>
                setEditNotesModal((prev) => ({ ...prev, notesText: e.target.value }))
              }
              placeholder="Enter notes or instructions for this purchase order..."
            />
          </FormGroup>
          <Alert color="info" className="mt-2 mb-0">
            You can edit notes only while the PO status is <strong>submitted</strong> and approval status is <strong>pending</strong>.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseEditNotes}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSaveNotes}
            disabled={isUpdatingNotes}
          >
            {isUpdatingNotes ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="ri-check-line me-1" />
                Save Notes
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Summary Card at Bottom */}
      <Card className="mt-4 border-0 bg-light">
        <CardBody className="p-3">
          <Row className="text-center">
            <Col md={3}>
              <div className="border-end">
                <h4 className="text-primary mb-1">{purchaseOrdersData.total_purchase_orders}</h4>
                <small className="text-muted">Total POs</small>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-end">
                <h4 className="text-success mb-1">
                  {purchaseOrdersData.purchase_orders.filter((po: PurchaseOrder) => po.po_status === 'approved').length}
                </h4>
                <small className="text-muted">Approved</small>
              </div>
            </Col>
            <Col md={3}>
              <div className="border-end">
                <h4 className="text-warning mb-1">
                  {purchaseOrdersData.purchase_orders.filter((po: PurchaseOrder) => po.po_status === 'draft').length}
                </h4>
                <small className="text-muted">Draft</small>
              </div>
            </Col>
            <Col md={3}>
              <div>
                <h4 className="text-info mb-1">
                  {purchaseOrdersData.purchase_orders.reduce((total: number, po: PurchaseOrder) => total + po.items_count, 0)}
                </h4>
                <small className="text-muted">Total Items</small>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </div>
  );
};

export default PurchaseOrderList;